import {
  type ChronologicalCommentsSort,
  isChronologicalCommentsSort,
} from '@github-ui/commenting/ChronologicalCommentsSort'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {useMemo} from 'react'

import {type ActivityFilterCategory, isActivityFilterCategory, normalizeActivityFilters} from './activity-filters'

// Plain type guards rather than typebox: Memex loads this code under Jest, which fakes typebox.

// v2 since the categories were regrouped, so older selections start over with everything shown.
export const ACTIVITY_FILTERS_STORAGE_KEY = 'issue-activity-filters-v2'
export const ACTIVITY_SORT_STORAGE_KEY = 'issue-activity-sort'

export type ActivitySort = ChronologicalCommentsSort

export const NO_ACTIVITY_FILTERS: ActivityFilterCategory[] = []

// Any strings, so a removed category drops out without clearing the rest.
function isSavedActivityFilters(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string')
}

function isSavedActivitySort(value: unknown): value is ActivitySort | null {
  return value === null || isChronologicalCommentsSort(value)
}

/** Activity filter categories, shared by every issue. Empty means everything is shown. */
export function useActivityFilters() {
  const [filters, setFilters] = useLocalStorage<readonly string[]>(ACTIVITY_FILTERS_STORAGE_KEY, NO_ACTIVITY_FILTERS, {
    validator: isSavedActivityFilters,
  })

  // Kept stable: the timeline's query keys and live-update config are built from it.
  const normalized = useMemo(() => normalizeActivityFilters(filters.filter(isActivityFilterCategory)), [filters])

  return [normalized, setFilters] as const
}

/** The Activity sort the reader picked, shared by every issue. `null` until they pick one. */
export function useActivitySort() {
  const [sort, setSort] = useLocalStorage<ActivitySort | null>(ACTIVITY_SORT_STORAGE_KEY, null, {
    validator: isSavedActivitySort,
  })

  return [sort, setSort] as const
}
